interface SpecialistRecommendation {
  specialty: string;
  description: string;
  whenToConsider: string[];
  urgencyLevel: 'routine' | 'prompt' | 'urgent';
  searchUrl: string;
  estimatedWaitTime: string;
  relevantDiagnoses?: string[];
  specificReasoning?: string;
  priority?: number;
}

interface DiagnosisBasedSpecialist {
  condition: string;
  specialists: Array<{
    specialty: string;
    urgency: 'routine' | 'prompt' | 'urgent';
    reasoning: string;
    priority: number;
  }>;
}

export class SpecialistRecommendationService {
  private specialtyMapping: Record<string, SpecialistRecommendation[]> = {
    'Head and Nerves': [
      {
        specialty: 'Neurologist',
        description: 'Specialist in disorders of the nervous system, brain, and spinal cord',
        whenToConsider: [
          'Recurrent or severe headaches',
          'Persistent dizziness or balance problems',
          'Memory or cognitive concerns',
          'Seizures or neurological symptoms',
          'Chronic pain with neurological features'
        ],
        urgencyLevel: 'prompt',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '4-12 weeks'
      },
      {
        specialty: 'ENT (Ear, Nose, Throat)',
        description: 'Specialist in ear, nose, throat, and related head and neck disorders',
        whenToConsider: [
          'Persistent dizziness or vertigo',
          'Hearing problems',
          'Chronic sinus issues',
          'Balance disorders',
          'Head and neck concerns'
        ],
        urgencyLevel: 'routine',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '6-18 weeks'
      },
      {
        specialty: 'Ophthalmologist',
        description: 'Eye specialist for vision and eye-related problems',
        whenToConsider: [
          'Vision changes with headaches',
          'Eye pain or visual disturbances',
          'Sudden vision loss',
          'Eye-related symptoms',
          'Need for comprehensive eye examination'
        ],
        urgencyLevel: 'prompt',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '4-8 weeks'
      }
    ],
    'Respiratory': [
      {
        specialty: 'Pulmonologist',
        description: 'Specialist in lung and respiratory system disorders',
        whenToConsider: [
          'Chronic cough or breathing difficulties',
          'Suspected asthma or COPD',
          'Recurrent respiratory infections',
          'Chest pain with breathing',
          'Need for pulmonary function testing'
        ],
        urgencyLevel: 'prompt',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '4-10 weeks'
      },
      {
        specialty: 'Allergist/Immunologist',
        description: 'Specialist in allergies and immune system disorders',
        whenToConsider: [
          'Suspected allergic triggers for symptoms',
          'Recurrent respiratory allergies',
          'Food or environmental allergies',
          'Immune system concerns',
          'Need for allergy testing'
        ],
        urgencyLevel: 'routine',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '8-16 weeks'
      }
    ],
    'Digestive': [
      {
        specialty: 'Gastroenterologist',
        description: 'Specialist in digestive system and gastrointestinal disorders',
        whenToConsider: [
          'Chronic digestive problems',
          'Persistent abdominal pain',
          'Changes in bowel habits',
          'Suspected inflammatory bowel disease',
          'Need for endoscopic procedures'
        ],
        urgencyLevel: 'prompt',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '6-12 weeks'
      },
      {
        specialty: 'Hepatologist',
        description: 'Specialist in liver disorders and diseases',
        whenToConsider: [
          'Liver function abnormalities',
          'Persistent nausea with liver concerns',
          'Jaundice or liver-related symptoms',
          'Alcohol-related liver issues',
          'Hepatitis or liver disease management'
        ],
        urgencyLevel: 'prompt',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '4-8 weeks'
      }
    ],
    'Musculoskeletal': [
      {
        specialty: 'Rheumatologist',
        description: 'Specialist in arthritis and autoimmune musculoskeletal conditions',
        whenToConsider: [
          'Joint pain affecting multiple joints',
          'Suspected rheumatoid arthritis',
          'Autoimmune conditions',
          'Chronic inflammatory conditions',
          'Morning stiffness and joint swelling'
        ],
        urgencyLevel: 'prompt',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '6-14 weeks'
      },
      {
        specialty: 'Orthopaedic Surgeon',
        description: 'Specialist in bone, joint, and musculoskeletal surgery',
        whenToConsider: [
          'Severe joint or bone problems',
          'Sports injuries requiring specialist care',
          'Fractures or structural problems',
          'Need for surgical evaluation',
          'Chronic pain not responding to conservative treatment'
        ],
        urgencyLevel: 'routine',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '8-18 weeks'
      },
      {
        specialty: 'Physiotherapist',
        description: 'Movement and exercise specialist for musculoskeletal rehabilitation',
        whenToConsider: [
          'Muscle or joint mobility issues',
          'Rehabilitation after injury',
          'Chronic pain management',
          'Posture and movement problems',
          'Exercise and strengthening needs'
        ],
        urgencyLevel: 'routine',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '2-6 weeks'
      }
    ],
    'Skin': [
      {
        specialty: 'Dermatologist',
        description: 'Specialist in skin, hair, and nail disorders',
        whenToConsider: [
          'Persistent or recurring skin conditions',
          'Unusual rashes or skin changes',
          'Chronic eczema or psoriasis',
          'Skin cancer screening',
          'Complex allergic skin reactions'
        ],
        urgencyLevel: 'routine',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '6-16 weeks'
      },
      {
        specialty: 'Allergist/Immunologist',
        description: 'Specialist in allergic reactions and immune system disorders',
        whenToConsider: [
          'Suspected allergic triggers for skin symptoms',
          'Recurrent allergic reactions',
          'Food or contact allergies',
          'Immune system-related skin conditions',
          'Need for comprehensive allergy testing'
        ],
        urgencyLevel: 'routine',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '8-16 weeks'
      }
    ],
    'General': [
      {
        specialty: 'Internal Medicine',
        description: 'General medical specialist for complex medical conditions',
        whenToConsider: [
          'Multiple system involvement',
          'Complex diagnostic challenges',
          'Chronic medical conditions',
          'Need for comprehensive medical evaluation',
          'Coordination of multiple specialists'
        ],
        urgencyLevel: 'routine',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '4-10 weeks'
      },
      {
        specialty: 'Endocrinologist',
        description: 'Specialist in hormonal and metabolic disorders',
        whenToConsider: [
          'Unexplained fatigue with metabolic concerns',
          'Thyroid or hormonal problems',
          'Diabetes management',
          'Weight changes with hormonal symptoms',
          'Adrenal or pituitary concerns'
        ],
        urgencyLevel: 'routine',
        searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
        estimatedWaitTime: '6-12 weeks'
      }
    ]
  };

  // Comprehensive diagnosis-to-specialist mapping
  private diagnosisSpecialistMapping: Record<string, DiagnosisBasedSpecialist> = {
    // Cardiovascular conditions
    'myocardial infarction': {
      condition: 'Myocardial Infarction',
      specialists: [
        { specialty: 'Cardiologist', urgency: 'urgent', reasoning: 'Heart attack requires immediate cardiac specialist evaluation', priority: 1 },
        { specialty: 'Emergency Medicine', urgency: 'urgent', reasoning: 'Immediate emergency care needed', priority: 1 }
      ]
    },
    'atrial fibrillation': {
      condition: 'Atrial Fibrillation',
      specialists: [
        { specialty: 'Cardiologist', urgency: 'urgent', reasoning: 'Irregular heart rhythm needs cardiac specialist care', priority: 1 },
        { specialty: 'Electrophysiologist', urgency: 'prompt', reasoning: 'Heart rhythm specialist for arrhythmia management', priority: 2 }
      ]
    },
    'heart failure': {
      condition: 'Heart Failure',
      specialists: [
        { specialty: 'Cardiologist', urgency: 'urgent', reasoning: 'Heart failure requires specialized cardiac management', priority: 1 }
      ]
    },
    'pulmonary embolism': {
      condition: 'Pulmonary Embolism',
      specialists: [
        { specialty: 'Pulmonologist', urgency: 'urgent', reasoning: 'Blood clot in lungs requires urgent respiratory specialist care', priority: 1 },
        { specialty: 'Hematologist', urgency: 'prompt', reasoning: 'Blood clotting disorders need blood specialist evaluation', priority: 2 }
      ]
    },
    'deep vein thrombosis': {
      condition: 'Deep Vein Thrombosis',
      specialists: [
        { specialty: 'Vascular Surgeon', urgency: 'urgent', reasoning: 'Blood clot in leg requires vascular specialist care', priority: 1 },
        { specialty: 'Hematologist', urgency: 'prompt', reasoning: 'Clotting disorder evaluation needed', priority: 2 }
      ]
    },

    // Neurological conditions
    'stroke': {
      condition: 'Stroke',
      specialists: [
        { specialty: 'Neurologist', urgency: 'urgent', reasoning: 'Stroke requires immediate neurological specialist care', priority: 1 },
        { specialty: 'Emergency Medicine', urgency: 'urgent', reasoning: 'Emergency treatment needed', priority: 1 }
      ]
    },
    'transient ischemic attack': {
      condition: 'Transient Ischemic Attack',
      specialists: [
        { specialty: 'Neurologist', urgency: 'urgent', reasoning: 'TIA requires urgent neurological evaluation to prevent stroke', priority: 1 }
      ]
    },
    'seizure': {
      condition: 'Seizure Disorder',
      specialists: [
        { specialty: 'Neurologist', urgency: 'urgent', reasoning: 'Seizures require neurological specialist evaluation', priority: 1 },
        { specialty: 'Epileptologist', urgency: 'prompt', reasoning: 'Seizure specialist for epilepsy management', priority: 2 }
      ]
    },
    'migraine': {
      condition: 'Migraine',
      specialists: [
        { specialty: 'Neurologist', urgency: 'prompt', reasoning: 'Chronic headaches benefit from neurological specialist care', priority: 1 },
        { specialty: 'Headache Specialist', urgency: 'routine', reasoning: 'Specialized headache treatment', priority: 2 }
      ]
    },

    // Respiratory conditions
    'pneumonia': {
      condition: 'Pneumonia',
      specialists: [
        { specialty: 'Pulmonologist', urgency: 'prompt', reasoning: 'Lung infection may require respiratory specialist care', priority: 1 },
        { specialty: 'Infectious Disease', urgency: 'prompt', reasoning: 'Complex infections need specialist evaluation', priority: 2 }
      ]
    },
    'asthma': {
      condition: 'Asthma',
      specialists: [
        { specialty: 'Pulmonologist', urgency: 'routine', reasoning: 'Chronic respiratory condition benefits from lung specialist care', priority: 1 },
        { specialty: 'Allergist/Immunologist', urgency: 'routine', reasoning: 'Allergy triggers assessment and management', priority: 2 }
      ]
    },

    // Gastrointestinal conditions
    'appendicitis': {
      condition: 'Appendicitis',
      specialists: [
        { specialty: 'General Surgeon', urgency: 'urgent', reasoning: 'Appendicitis requires urgent surgical evaluation', priority: 1 },
        { specialty: 'Emergency Medicine', urgency: 'urgent', reasoning: 'Emergency care needed', priority: 1 }
      ]
    },
    'inflammatory bowel disease': {
      condition: 'Inflammatory Bowel Disease',
      specialists: [
        { specialty: 'Gastroenterologist', urgency: 'prompt', reasoning: 'IBD requires specialized gastroenterology care', priority: 1 }
      ]
    },
    'peptic ulcer': {
      condition: 'Peptic Ulcer Disease',
      specialists: [
        { specialty: 'Gastroenterologist', urgency: 'prompt', reasoning: 'Stomach ulcers need GI specialist evaluation', priority: 1 }
      ]
    },

    // Psychiatric conditions
    'major depressive disorder': {
      condition: 'Major Depressive Disorder',
      specialists: [
        { specialty: 'Psychiatrist', urgency: 'prompt', reasoning: 'Depression benefits from psychiatric specialist care', priority: 1 },
        { specialty: 'Psychologist', urgency: 'routine', reasoning: 'Therapy and counseling support', priority: 2 }
      ]
    },
    'anxiety disorder': {
      condition: 'Anxiety Disorder',
      specialists: [
        { specialty: 'Psychiatrist', urgency: 'routine', reasoning: 'Anxiety disorders benefit from psychiatric evaluation', priority: 1 },
        { specialty: 'Psychologist', urgency: 'routine', reasoning: 'Cognitive behavioral therapy support', priority: 2 }
      ]
    },

    // Endocrine conditions
    'diabetes': {
      condition: 'Diabetes Mellitus',
      specialists: [
        { specialty: 'Endocrinologist', urgency: 'prompt', reasoning: 'Diabetes requires specialized hormone specialist care', priority: 1 }
      ]
    },
    'thyroid disorder': {
      condition: 'Thyroid Disorder',
      specialists: [
        { specialty: 'Endocrinologist', urgency: 'routine', reasoning: 'Thyroid conditions need hormone specialist evaluation', priority: 1 }
      ]
    }
  };

  // Enhanced specialist database with more specialties
  private enhancedSpecialtyDatabase: Record<string, SpecialistRecommendation> = {
    'Cardiologist': {
      specialty: 'Cardiologist',
      description: 'Heart and cardiovascular system specialist',
      whenToConsider: ['Heart conditions', 'Chest pain', 'Palpitations', 'High blood pressure'],
      urgencyLevel: 'prompt',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '4-8 weeks'
    },
    'Pulmonologist': {
      specialty: 'Pulmonologist', 
      description: 'Lung and respiratory system specialist',
      whenToConsider: ['Breathing problems', 'Lung conditions', 'Chronic cough'],
      urgencyLevel: 'prompt',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '4-10 weeks'
    },
    'Neurologist': {
      specialty: 'Neurologist',
      description: 'Brain, spinal cord, and nervous system specialist',
      whenToConsider: ['Neurological symptoms', 'Headaches', 'Seizures', 'Memory problems'],
      urgencyLevel: 'prompt',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '6-14 weeks'
    },
    'Gastroenterologist': {
      specialty: 'Gastroenterologist',
      description: 'Digestive system and gastrointestinal specialist',
      whenToConsider: ['Digestive problems', 'Abdominal pain', 'Bowel issues'],
      urgencyLevel: 'prompt',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '6-12 weeks'
    },
    'Psychiatrist': {
      specialty: 'Psychiatrist',
      description: 'Mental health and psychiatric specialist',
      whenToConsider: ['Mental health conditions', 'Depression', 'Anxiety', 'Psychiatric symptoms'],
      urgencyLevel: 'prompt',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '4-12 weeks'
    },
    'Endocrinologist': {
      specialty: 'Endocrinologist',
      description: 'Hormone and metabolic system specialist',
      whenToConsider: ['Hormonal disorders', 'Diabetes', 'Thyroid problems'],
      urgencyLevel: 'routine',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '6-12 weeks'
    },
    'Emergency Medicine': {
      specialty: 'Emergency Medicine',
      description: 'Emergency and urgent care specialist',
      whenToConsider: ['Emergency conditions', 'Life-threatening symptoms'],
      urgencyLevel: 'urgent',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: 'Immediate'
    },
    'General Surgeon': {
      specialty: 'General Surgeon',
      description: 'Surgical specialist for general surgical conditions',
      whenToConsider: ['Surgical conditions', 'Appendicitis', 'Hernias'],
      urgencyLevel: 'prompt',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '4-10 weeks'
    },
    'Vascular Surgeon': {
      specialty: 'Vascular Surgeon',
      description: 'Blood vessel and circulation specialist',
      whenToConsider: ['Blood clots', 'Circulation problems', 'Vascular conditions'],
      urgencyLevel: 'prompt',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '6-12 weeks'
    },
    'Hematologist': {
      specialty: 'Hematologist',
      description: 'Blood disorders and clotting specialist',
      whenToConsider: ['Blood disorders', 'Clotting problems', 'Anemia'],
      urgencyLevel: 'prompt',
      searchUrl: 'https://www.nhs.uk/nhs-services/hospitals/referrals-for-specialist-care/',
      estimatedWaitTime: '4-8 weeks'
    }
  };

  /**
   * Get AI-diagnosis-based specialist recommendations
   */
  getSpecialistsByAIDiagnoses(
    potentialDiagnoses: Array<{
      condition: string;
      probability: number;
      urgencyLevel: string;
      reasoning: string;
    }>,
    aiScore: number
  ): SpecialistRecommendation[] {
    const recommendedSpecialists: Array<SpecialistRecommendation & { priority: number; specificReasoning: string }> = [];

    // Process each AI diagnosis
    for (const diagnosis of potentialDiagnoses) {
      const conditionLower = diagnosis.condition.toLowerCase();
      
      // Find matching specialists for this diagnosis
      for (const [key, mappingData] of Object.entries(this.diagnosisSpecialistMapping)) {
        if (conditionLower.includes(key) || key.includes(conditionLower.split(' ')[0])) {
          for (const specialistData of mappingData.specialists) {
            const baseSpecialist = this.enhancedSpecialtyDatabase[specialistData.specialty];
            if (baseSpecialist) {
              // Adjust urgency based on AI score and diagnosis urgency
              let adjustedUrgency = specialistData.urgency;
              if (aiScore >= 80 || diagnosis.urgencyLevel === 'emergency') {
                adjustedUrgency = 'urgent';
              } else if (aiScore >= 60 || diagnosis.urgencyLevel === 'urgent') {
                adjustedUrgency = adjustedUrgency === 'routine' ? 'prompt' : adjustedUrgency;
              }

              recommendedSpecialists.push({
                ...baseSpecialist,
                urgencyLevel: adjustedUrgency,
                priority: specialistData.priority,
                specificReasoning: `Based on ${diagnosis.condition} (${diagnosis.probability}% probability): ${specialistData.reasoning}`,
                relevantDiagnoses: [diagnosis.condition]
              });
            }
          }
        }
      }
    }

    // Remove duplicates and sort by priority
    const uniqueSpecialists = recommendedSpecialists
      .filter((specialist, index, self) =>
        index === self.findIndex(s => s.specialty === specialist.specialty)
      )
      .sort((a, b) => {
        // Sort by urgency first, then priority
        const urgencyOrder = { urgent: 0, prompt: 1, routine: 2 };
        const urgencyDiff = urgencyOrder[a.urgencyLevel] - urgencyOrder[b.urgencyLevel];
        if (urgencyDiff !== 0) return urgencyDiff;
        return a.priority - b.priority;
      });

    return uniqueSpecialists.slice(0, 4); // Return top 4 most relevant specialists
  }

  // Legacy methods for backward compatibility
  getRecommendedSpecialists(
    category: string, 
    symptoms: string[], 
    aiScore: number,
    potentialDiagnoses?: string[]
  ): SpecialistRecommendation[] {
    const categorySpecialists = this.specialtyMapping[category] || this.specialtyMapping['General'];
    
    if (aiScore >= 60) {
      // For higher scores, prioritize specialists and adjust urgency
      return categorySpecialists.map(specialist => ({
        ...specialist,
        urgencyLevel: specialist.urgencyLevel === 'routine' ? 'prompt' : specialist.urgencyLevel
      }));
    }

    // Filter specialists based on specific symptoms
    const relevantSpecialists = categorySpecialists.filter(specialist => {
      return specialist.whenToConsider.some(condition => 
        symptoms.some(symptom => 
          condition.toLowerCase().includes(symptom.toLowerCase()) ||
          symptom.toLowerCase().includes(condition.toLowerCase())
        ) ||
        (potentialDiagnoses && potentialDiagnoses.some(diagnosis =>
          condition.toLowerCase().includes(diagnosis.toLowerCase())
        ))
      );
    });

    // If no specific matches, return the most general specialist for the category
    if (relevantSpecialists.length === 0) {
      return [categorySpecialists[0]];
    }

    return relevantSpecialists;
  }

  getSpecialistsByDiagnosis(diagnoses: string[]): SpecialistRecommendation[] {
    const specialists: SpecialistRecommendation[] = [];
    
    for (const diagnosis of diagnoses) {
      const lowerDiagnosis = diagnosis.toLowerCase();
      
      // Match diagnoses to appropriate specialists
      if (lowerDiagnosis.includes('headache') || lowerDiagnosis.includes('migraine')) {
        specialists.push(...this.specialtyMapping['Head and Nerves'].filter(s => s.specialty === 'Neurologist'));
      }
      
      if (lowerDiagnosis.includes('asthma') || lowerDiagnosis.includes('copd') || lowerDiagnosis.includes('pneumonia')) {
        specialists.push(...this.specialtyMapping['Respiratory'].filter(s => s.specialty === 'Pulmonologist'));
      }
      
      if (lowerDiagnosis.includes('arthritis') || lowerDiagnosis.includes('rheumat')) {
        specialists.push(...this.specialtyMapping['Musculoskeletal'].filter(s => s.specialty === 'Rheumatologist'));
      }
      
      if (lowerDiagnosis.includes('gastro') || lowerDiagnosis.includes('ibs') || lowerDiagnosis.includes('crohn')) {
        specialists.push(...this.specialtyMapping['Digestive'].filter(s => s.specialty === 'Gastroenterologist'));
      }
      
      if (lowerDiagnosis.includes('eczema') || lowerDiagnosis.includes('psoriasis') || lowerDiagnosis.includes('dermatitis')) {
        specialists.push(...this.specialtyMapping['Skin'].filter(s => s.specialty === 'Dermatologist'));
      }
    }

    // Remove duplicates
    const uniqueSpecialists = specialists.filter((specialist, index, self) =>
      index === self.findIndex(s => s.specialty === specialist.specialty)
    );

    return uniqueSpecialists;
  }

  getEmergencyContacts(): Array<{name: string; number: string; description: string; action: string}> {
    return [
      { 
        name: "Emergency Services", 
        number: "999", 
        description: "Life-threatening emergencies", 
        action: "tel:999" 
      },
      { 
        name: "NHS 111", 
        number: "111", 
        description: "24/7 Urgent Medical Advice", 
        action: "tel:111" 
      },
      { 
        name: "Crisis Support", 
        number: "116 123", 
        description: "Samaritans - Mental Health Crisis", 
        action: "tel:116123" 
      }
    ];
  }

  getPrimaryContacts(): Array<{name: string; number: string; description: string; action: string}> {
    return [
      { 
        name: "Book GP Appointment", 
        number: "", 
        description: "Register & Book Online", 
        action: "https://www.nhs.uk/nhs-services/gps/how-to-register-with-a-gp-surgery/" 
      },
      { 
        name: "NHS App", 
        number: "", 
        description: "Access Health Records & Services", 
        action: "https://www.nhs.uk/nhs-app/" 
      },
      { 
        name: "Patient Advice", 
        number: "0300 311 22 33", 
        description: "Patient Advice & Liaison Service", 
        action: "tel:03003112233" 
      }
    ];
  }
}

export const specialistRecommendationService = new SpecialistRecommendationService();